import { api, mutate } from './api'
import { worksAlone } from './me'
import { clockOf } from './job-times'
import { noteNode } from './note-lines'
import { roundDay } from './round-config'
import type { TodayClean } from './types'
import { addDaysISO, clear, dayLabel, el, shortDate } from './ui'
import { askedCard, parkingCard, type AskedVisit, type ParkingAsked } from './extras-asked'

// Worker notes (0071; Liam, 13 Oct 2026: "it should make the access and notes collapsible if its added and new 'worker
// notes' is added for us to triage and see later as the owners"; the designs approved the same evening).
//   On Today, a finished job's note sits in its own lemon box, WORKER NOTE, with who wrote it and when, and the job's
//   Access & notes fold into one line, so the note just added is what shows (workerNoteBox, foldedAccessNotes).
//   An owner's Today has "Worker notes to look at" at the top while any wait (workerNotesBar); it opens the list, where
//   each note is Seen (out of the list) or kept for next time (added to the customer's Access & notes and Seen), each
//   with Undo (openWorkerNotes). The server's side: functions/api/worker-notes.

export interface WorkerNote {
  id: number
  customer_id: number
  customer_name: string
  date: string
  note: string
  note_at: string
  note_by_name: string | null
  note_seen_at: string | null
  note_remind?: number
  worker: string | null
}

// "today 6:03pm", "yesterday 6:03pm", or the day: when a note was written, on the round's clock.
export function noteWhen(noteAt: string): string {
  const d = new Date(`${noteAt.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return ''
  const day = roundDay(d), today = roundDay()
  if (day === today) return `today ${clockOf(noteAt)}`
  if (day === addDaysISO(today, -1)) return `yesterday ${clockOf(noteAt)}`
  return dayLabel(day)
}

function whoWhen(by: string | null | undefined, at: string | null | undefined): string {
  return [by?.trim(), at ? noteWhen(at) : ''].filter(Boolean).join(' · ')
}

// A finished job's note on its Today card: a yellow sticky note on the green card (Liam, 14 Oct 2026: "im wondering if
// we make the note yellow"), the words first, then who wrote it and when, small, with Change.
export function workerNoteBox(cl: TodayClean, onChange: () => void): HTMLElement {
  const change = el('button', { type: 'button', class: 'link-button worker-note-change', 'aria-label': `Change the note for ${cl.customer_name}` }, 'Change')
  change.addEventListener('click', onChange)
  const by = cl.note_by_name?.trim()
  const when = cl.note_at ? noteWhen(cl.note_at) : ''
  const meta = el('div', { class: 'worker-note-meta' }, el('span', { class: 'worker-note-label' }, [by, when].filter(Boolean).join(' · ') || 'Note'), change)
  return el('div', { class: 'worker-note-box' }, el('p', { class: 'worker-note-tile' }, noteNode(cl.note ?? '')), meta)
}

// From last time (0072): on the customer's next visit, the note the owner chose to show once, in the same lemon shape,
// headed with who wrote it and the day. Gone once that visit is done (the server stops sending it).
export function lastTimeBox(cl: TodayClean): HTMLElement | null {
  if (!cl.last_time) return null
  let v: { note?: string | null; by?: string | null; date?: string | null } = {}
  try { v = JSON.parse(cl.last_time) as typeof v } catch { return null }
  const note = v.note?.trim()
  if (!note) return null
  const label = el('span', { class: 'worker-note-label last-time-label' }, ['From last time', v.by?.trim(), v.date ? shortDate(v.date) : ''].filter(Boolean).join(' · '))
  return el('div', { class: 'worker-note-box last-time-box' }, label, el('p', { class: 'worker-note-tile' }, noteNode(note)))
}

// A finished job's Access & notes, folded to a plain line, no box (Liam, 14 Oct 2026: calmer finished cards): a tap
// opens them.
export function foldedAccessNotes(rows: HTMLElement[]): HTMLElement {
  const summary = el('summary', { class: 'access-notes-fold' }, el('span', {}, `Access & notes (${rows.length})`), el('span', { class: 'access-notes-arrow', 'aria-hidden': 'true' }))
  return el('details', { class: 'access-notes-folded' }, summary, el('div', { class: 'access-notes access-notes-opened' }, ...rows))
}

// A one-person round (0078; Liam, 19 Oct 2026): the notes are the owner's own, so the list is "Your job notes", with the
// same Seen, Next visit and Every visit. Exported for the computer's Needs you line.
export function notesTitle(): string {
  return worksAlone() ? 'Your job notes' : 'Worker notes'
}

// ---- the owner's bar on Today -------------------------------------------------------------------------------------

let lastCount = 0

export function workerNotesBar(): HTMLElement {
  // An empty place on the page until a note waits: nothing to see or hear while there are none.
  const slot = el('div', { class: 'worker-notes-slot' })
  const draw = (): void => {
    clear(slot)
    if (!lastCount) return
    const words = `${notesTitle()} to look at`
    const bar = el('button', { type: 'button', class: 'worker-notes-bar', 'aria-label': `${words}: ${lastCount}` },
      el('span', { class: 'worker-notes-bar-words' }, words), el('span', { class: 'worker-notes-count' }, String(lastCount)),
      el('span', { class: 'worker-notes-arrow', 'aria-hidden': 'true' }))
    // A change made there reads Today again (the list sends dropby:data-changed), which draws a fresh bar.
    bar.addEventListener('click', () => openWorkerNotes(() => void refresh()))
    slot.append(bar)
  }
  const refresh = async (): Promise<void> => {
    try {
      const r = await api<{ new_count: number }>('/api/worker-notes')
      if (r.new_count !== lastCount || !slot.childElementCount) { lastCount = r.new_count; draw() }
    } catch {
      // No signal or not an owner: the bar stays as it was.
    }
  }
  draw()
  void refresh()
  return slot
}

// ---- the list --------------------------------------------------------------------------------------------------------

export function openWorkerNotes(onClosed: () => void): void {
  const dialog = el('dialog', { class: 'worker-notes-sheet', 'aria-labelledby': 'worker-notes-title' }) as HTMLDialogElement
  const close = el('button', { type: 'button', class: 'btn-secondary' }, 'Close')
  close.addEventListener('click', () => dialog.close())
  const fresh = el('button', { type: 'button', class: 'worker-notes-tab', 'aria-pressed': 'true' }, 'New')
  const seen = el('button', { type: 'button', class: 'worker-notes-tab', 'aria-pressed': 'false' }, 'Seen')
  const error = el('p', { class: 'form-error', role: 'alert' })
  const list = el('div', { class: 'worker-notes-list' })
  dialog.append(
    el('header', { class: 'worker-notes-head' }, el('h2', { id: 'worker-notes-title' }, notesTitle()), close),
    el('div', { class: 'worker-notes-tabs', role: 'group', 'aria-label': 'Which notes' }, fresh, seen),
    error, list)
  let showing: 'new' | 'seen' = 'new'
  let changed = false
  // What the changes are called while they wait to send.
  const noteWord = worksAlone() ? 'Job note' : 'Worker note'

  const act = async (n: WorkerNote, action: 'seen' | 'remind' | 'new' | 'keep', item: HTMLElement): Promise<void> => {
    error.textContent = ''
    for (const b of item.querySelectorAll('button')) b.setAttribute('disabled', 'disabled')
    const ack = await mutate<{ kept?: boolean; job_notes_before?: string | null; customer_version?: number }>({ label: `${noteWord}: ${n.customer_name} ${n.date}`, method: 'POST', path: `/api/worker-notes/${n.id}`, body: { action, note_at: n.note_at } })
    if (!ack.ok && ack.status !== 0) {
      for (const b of item.querySelectorAll('button')) b.removeAttribute('disabled')
      error.textContent = ack.status === 409 ? `${n.customer_name}'s note was written again since, so it is shown again.` : ack.error ?? "That didn't save."
      if (ack.status === 409) void load()
      return
    }
    changed = true
    // The note slides out and a line takes its place with Undo, inside the list (the page's own Undo bar sits behind an
    // open list, where it cannot be tapped). The line goes after a few seconds.
    const back = async (): Promise<void> => {
      await mutate({ label: `${noteWord} back: ${n.customer_name} ${n.date}`, method: 'POST', path: `/api/worker-notes/${n.id}`, body: { action: 'new', note_at: n.note_at } })
      changed = true
      void load()
    }
    let words = `Back in New: ${n.customer_name}`
    let undo: (() => Promise<void>) | null = null
    if (action === 'seen') { words = `Seen: ${n.customer_name}`; undo = back }
    else if (action === 'remind') { words = `On ${n.customer_name}'s next visit`; undo = back }
    else if (action === 'keep' && ack.row?.kept && typeof ack.row.customer_version === 'number') {
      const version = ack.row.customer_version, before = ack.row.job_notes_before ?? null
      words = `On every visit to ${n.customer_name}`
      undo = async () => {
        await mutate({ label: `Undo keep: ${n.customer_name}`, method: 'PATCH', path: `/api/customers/${n.customer_id}`, body: { job_notes: before, version } })
        await back()
      }
    } else if (action === 'keep') { words = `Seen: ${n.customer_name}. It was already in their notes.`; undo = back }
    item.classList.add('worker-notes-going')
    window.setTimeout(() => {
      const line = el('p', { class: 'worker-notes-done', role: 'status' }, el('span', {}, words))
      if (undo) {
        const u = el('button', { type: 'button', class: 'link-button worker-notes-undo' }, 'Undo')
        u.addEventListener('click', () => { u.setAttribute('disabled', 'disabled'); void undo!() })
        line.append(u)
      }
      item.replaceWith(line)
      window.setTimeout(() => { line.remove(); if (!list.querySelector('.worker-notes-item, .worker-notes-done')) void load() }, 6000)
    }, 180)
  }

  const item = (n: WorkerNote): HTMLElement => {
    const by = n.note_by_name?.trim() || n.worker?.trim() || ''
    const name = el('a', { class: 'worker-notes-customer', href: `#/customer/${n.customer_id}` }, n.customer_name)
    name.addEventListener('click', () => dialog.close())
    const row = el('article', { class: 'worker-notes-item' },
      el('div', { class: 'worker-notes-item-head' }, el('span', { class: 'worker-notes-initial', 'aria-hidden': 'true' }, (by || '?').charAt(0).toUpperCase()), name,
        el('span', { class: 'worker-notes-who' }, whoWhen(by, n.note_at))),
      el('div', { class: 'worker-notes-text' }, noteNode(n.note)))
    const acts = el('div', { class: 'worker-notes-acts' })
    if (showing === 'new') {
      // Show it again? Seen: this visit only. Next visit: once, as "From last time". Every visit: into Access & notes
      // (Liam, 14 Oct 2026, approved design).
      const choice = (action: 'seen' | 'remind' | 'keep', label: string, sub: string, cls: string): HTMLElement => {
        const b = el('button', { type: 'button', class: `worker-notes-choice ${cls}` }, el('strong', {}, label), el('span', {}, sub))
        b.addEventListener('click', () => void act(n, action, row))
        return b
      }
      row.append(el('span', { class: 'worker-notes-ask' }, 'Show it again?'))
      acts.classList.add('worker-notes-choices')
      acts.append(choice('seen', 'Seen', 'this visit only', 'worker-notes-seen'), choice('remind', 'Next visit', 'a one-time reminder', 'worker-notes-remind'),
        choice('keep', 'Every visit', 'into Access & notes', 'worker-notes-keep'))
    } else {
      if (n.note_remind) row.append(el('span', { class: 'worker-notes-ask' }, 'Shows on their next visit'))
      const b = el('button', { type: 'button', class: 'worker-notes-keep' }, 'Back to new')
      b.addEventListener('click', () => void act(n, 'new', row))
      acts.append(b)
    }
    row.append(acts)
    return row
  }

  const load = async (): Promise<void> => {
    fresh.setAttribute('aria-pressed', String(showing === 'new'))
    seen.setAttribute('aria-pressed', String(showing === 'seen'))
    try {
      const r = await api<{ notes: WorkerNote[]; new_count: number; extras?: AskedVisit[]; parking?: ParkingAsked[] }>(`/api/worker-notes${showing === 'seen' ? '?seen=1' : ''}`)
      fresh.textContent = r.new_count ? `New ${r.new_count}` : 'New'
      clear(list)
      // A helper's extras waiting for a price (0091, src/extras-asked.ts), first: they change what is owed.
      for (const v of r.extras ?? []) {
        const card = askedCard(v, v.extras_asked_at ? noteWhen(v.extras_asked_at) : '', (words, undo) => {
          changed = true
          const line = el('p', { class: 'worker-notes-done', role: 'status' }, el('span', {}, words))
          const u = el('button', { type: 'button', class: 'link-button worker-notes-undo' }, 'Undo')
          u.addEventListener('click', () => { u.setAttribute('disabled', 'disabled'); void undo().then(() => { changed = true; void load() }) })
          line.append(u)
          card.replaceWith(line)
          window.setTimeout(() => line.remove(), 8000)
        }, (words) => { error.textContent = words })
        list.append(card)
      }
      for (const pk of r.parking ?? []) {
        const card = parkingCard(pk, pk.parking_asked_at ? noteWhen(pk.parking_asked_at) : '', (words, undo) => {
          changed = true
          const line = el('p', { class: 'worker-notes-done', role: 'status' }, el('span', {}, words))
          const u = el('button', { type: 'button', class: 'link-button worker-notes-undo' }, 'Undo')
          u.addEventListener('click', () => { u.setAttribute('disabled', 'disabled'); void undo().then(() => { changed = true; void load() }) })
          line.append(u)
          card.replaceWith(line)
          window.setTimeout(() => line.remove(), 8000)
        }, (words) => { error.textContent = words })
        list.append(card)
      }
      if (!r.notes.length && !(r.extras ?? []).length && !(r.parking ?? []).length) list.append(el('p', { class: 'muted worker-notes-empty' }, showing === 'new' ? (worksAlone() ? 'No new job notes. A note you add to a finished job shows here.' : 'No new worker notes. A note added to a finished job shows here.') : 'Nothing dealt with in the last 30 days.'))
      for (const n of r.notes) list.append(item(n))
    } catch (err) {
      error.textContent = `${notesTitle()} didn't load: ${(err as Error).message}`
    }
  }
  fresh.addEventListener('click', () => { showing = 'new'; void load() })
  seen.addEventListener('click', () => { showing = 'seen'; void load() })
  dialog.addEventListener('close', () => { dialog.remove(); if (changed) window.dispatchEvent(new CustomEvent('dropby:data-changed')); onClosed() })
  document.body.append(dialog)
  dialog.showModal()
  list.append(el('p', { class: 'muted' }, 'Loading…'))
  void load()
}
